import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Search, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { produceAPI } from '@/lib/api';
import { PRODUCE_CATEGORIES } from '@/data/produce';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import OfferCard, { fmtQty, unitLabel } from '@/components/produce/OfferCard';
import { useToast } from '@/hooks/use-toast';

/**
 * The farm produce market: what farmers and clusters have for sale now,
 * with the stock level on every result. Everyone signed in can browse;
 * offtakers can message the seller (the farmer, or the cluster manager).
 */
// The offtaker's buying area (PR 75) as market filters: whole states, and
// "State|LGA" for states where only some local areas were chosen.
function areaParams(area) {
  if (!area?.states?.length) return {};
  const whole = area.states.filter((s) => !(area.lgas?.[s] || []).length);
  const parts = area.states.flatMap((s) => (area.lgas?.[s] || []).map((l) => `${s}|${l}`));
  return { states: whole.join(','), lgas: parts.join(',') };
}

export default function ProduceMarketPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const myArea = user?.buying_area?.states?.length ? user.buying_area : null;
  const [kind, setKind] = useState('');
  const [sort, setSort] = useState('');
  const [onlyMyArea, setOnlyMyArea] = useState(Boolean(myArea));
  const [total, setTotal] = useState(0);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const category = params.get('category') || '';
  const [query, setQuery] = useState(q.trim());
  const [offers, setOffers] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [messaging, setMessaging] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(timer);
  }, [q]);

  const load = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const res = await produceAPI.market({
        q: query, category, kind, sort, limit: 30, offset, ...(onlyMyArea ? areaParams(myArea) : {}),
      });
      setOffers((prev) => (offset ? [...prev, ...res.data.offers] : res.data.offers));
      setHasMore(res.data.has_more);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  }, [query, category, kind, sort, onlyMyArea, myArea]);

  useEffect(() => { void load(0).catch(() => {}); }, [load]);

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c) next.set('category', c); else next.delete('category');
    setParams(next, { replace: true });
  };

  return (
    <div className="p-4 md:p-6 animate-fadeIn pb-8">
      <h1 className="font-heading text-2xl font-bold">{t('produceMarket.market.title')}</h1>
      <p className="text-muted-foreground mb-3">{t('produceMarket.market.subtitle')}</p>
      <div className="relative mb-3">
        <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-12 pl-10 bg-white text-base"
               placeholder={t('produceMarket.market.search')} data-testid="produce-search" />
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-2">
        {['', ...PRODUCE_CATEGORIES].map((c) => (
          <button key={c || 'all'} type="button" onClick={() => setCategory(c)}
                  className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold border ${
                    category === c ? 'bg-farm-primary text-white border-farm-primary' : 'bg-white text-gray-700'}`}
                  data-testid={`produce-cat-${c || 'all'}`}>
            {c ? t(`produce.categories.${c}`) : t('produceMarket.market.all')}
          </button>
        ))}
      </div>
      {/* Narrow the list down (PR 75): who sells it, where, and the order. */}
      <div className="rounded-2xl bg-white border p-3 mb-3 space-y-3" data-testid="produce-filters">
        <p className="text-sm font-semibold flex items-center gap-1.5"><SlidersHorizontal className="w-4 h-4" /> {t('produceMarket.filters.title')}</p>
        <div className="flex flex-wrap gap-2">
          {['', 'farmer', 'cluster'].map((k) => (
            <button key={k || 'all'} type="button" onClick={() => setKind(k)}
                    className={`rounded-full px-3 py-1.5 text-sm font-semibold border ${
                      kind === k ? 'bg-farm-forest text-white border-farm-forest' : 'bg-white text-gray-700'}`}
                    data-testid={`produce-kind-${k || 'all'}`}>
              {t(`produceMarket.filters.kind.${k || 'all'}`)}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {myArea && (
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" className="w-5 h-5 accent-farm-primary" checked={onlyMyArea}
                     onChange={(e) => setOnlyMyArea(e.target.checked)} data-testid="produce-my-area" />
              {t('produceMarket.filters.myArea')}
            </label>
          )}
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="produce-sort"
                  className="ml-auto h-10 rounded-lg border border-gray-300 bg-white px-2 text-sm">
            {['', 'newest', 'quantity', 'price'].map((o) => (
              <option key={o || 'name'} value={o}>{t(`produceMarket.filters.sort.${o || 'name'}`)}</option>
            ))}
          </select>
        </div>
      </div>
      {!loading && offers.length > 0 && (
        <p className="text-sm text-gray-600 mb-2" data-testid="produce-count">{t('produceMarket.filters.count', { count: total })}</p>
      )}
      <div className="grid md:grid-cols-2 gap-3" data-testid="produce-offers">
        {offers.map((offer) => <OfferCard key={`${offer.kind}-${offer.key}`} offer={offer} onMessage={setMessaging} />)}
      </div>
      {!loading && offers.length === 0 && <p className="text-center text-gray-600 py-10">{t('produceMarket.market.none')}</p>}
      {loading && <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-farm-primary" /></div>}
      {hasMore && !loading && (
        <Button variant="outline" className="w-full mt-3" onClick={() => load(offers.length)}>{t('produceMarket.market.more')}</Button>
      )}
      <MessageSellerDialog offer={messaging} onClose={() => setMessaging(null)} />
    </div>
  );
}

function MessageSellerDialog({ offer, onClose }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!offer) return;
    setBody(t('produceMarket.market.messageDefault', {
      item: offer.item, qty: fmtQty(offer.quantity, i18n.language), unit: unitLabel(t, offer.unit) }));
  }, [offer, t, i18n.language]);

  const send = async () => {
    setBusy(true);
    try {
      const res = await produceAPI.startChat(offer.contact_id, body.trim(), offer);
      toast({ title: t('produceMarket.market.sent') });
      onClose();
      navigate(`/produce/chats/${res.data.chat_id}`);
    } catch {
      toast({ title: t('produceMarket.market.sendFailed'), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!offer} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('produceMarket.market.messageTitle', {
            name: offer?.kind === 'cluster' ? `${offer.cluster_name} (${offer.contact_name})` : offer?.contact_name })}</DialogTitle>
        </DialogHeader>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} maxLength={2000}
                  className="w-full rounded-xl border p-3 text-base" data-testid="message-seller-body" />
        <Button className="w-full h-12 bg-farm-primary hover:bg-farm-primary-dark font-bold"
                disabled={busy || !body.trim()} onClick={send} data-testid="message-seller-send">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : t('produceMarket.market.send')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
